/* The phase certificate page (designs/products-pdp.md PP9/PP10/PP18 + UI
   "Certificate (Screen 6)"): the one deliberately different surface — a
   light paper page inside the dark app, because it is a frozen document,
   not a live view. Nothing here is editable; a superseding review marks it
   superseded (derived, PP10). It prints as one document through
   products-print.css. Pure: the owner fetches the PhaseCertificate, resolves
   role and feed-source names, and returns focus to the opening .cert-mark
   on Back. The document title carries PP18's print title while open. */
import { useEffect, useRef, type JSX } from "react";
import type { FeedReading } from "../../../../shared/feeds";
import { feedReadingLine, fullTime, valueText } from "./FeedRowValue";
import { DECISION_LABEL, fmtDate, fmtDateTime, stateText, type FrozenPointer, type PhaseCertificate } from "./model";
import { countsLine } from "./PhaseBars";
import { StateChip } from "./StateChip";

export interface CertificateProps {
  cert: PhaseCertificate;
  gateLabel: string;
  /* the gate's approver role name at issue; null = not recorded (red) */
  approverRoleName: string | null;
  /* role id → name, for the rows' Owner → Reviewer column */
  roleNames: Record<string, string>;
  /* feed source id → label; an unknown id shows as itself */
  feedSourceLabels?: Record<string, string>;
  onBack: () => void;
  /* defaults to window.print() */
  onPrint?: () => void;
}

const readingWords = (reading: FeedReading): string =>
  reading.state === "ok"
    ? `${valueText(reading.value, reading.unit)} at ${fullTime(reading.readAt)}`
    : reading.state === "unreachable"
      ? `unreachable since ${fullTime(reading.since)}`
      : "not read yet";

/* the Pointer at issue column: URLs shown as text (and linked), a feed by
   its source · measure · reading as frozen, an estimate by percent and note */
export function pointerWords(pointer: FrozenPointer, sourceLabels: Record<string, string>): { text: string; href: string | null } {
  if (pointer === null) return { text: "—", href: null };
  if (pointer.kind === "link") return { text: `${pointer.title} · ${pointer.url}`, href: pointer.url };
  if (pointer.kind === "estimate") return { text: `est. ${pointer.percent}% · "${pointer.note}"`, href: null };
  const source = sourceLabels[pointer.ref.sourceId] ?? pointer.ref.sourceId;
  return { text: `${source} · ${pointer.ref.measure} · ${readingWords(pointer.reading)}`, href: null };
}

const Missing = (): JSX.Element => <span className="state missing">not recorded</span>;

function Roles({ ids, names }: { ids: string[]; names: Record<string, string> }): JSX.Element {
  return ids.length ? <>{ids.map((id) => names[id] ?? id).join(" / ")}</> : <Missing />;
}

export function Certificate({ cert, gateLabel, approverRoleName, roleNames, feedSourceLabels = {}, onBack, onPrint }: CertificateProps): JSX.Element {
  const { frozen } = cert;
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previous = document.title;
    document.title = `${frozen.productName} — Phase ${cert.phase} certificate`;
    back.current?.focus();
    return () => {
      document.title = previous;
    };
  }, [frozen.productName, cert.phase]);

  const fed = frozen.criteria.filter((c) => c.feedReading !== null);

  return (
    <div className="cert-page">
      <div className="cert-actions">
        <button type="button" id="cert-back" className="btn ghost" ref={back} onClick={onBack}>
          ← Back to Phase
        </button>
        <button type="button" id="cert-print" className="btn ghost" onClick={() => (onPrint ? onPrint() : window.print())}>
          Print
        </button>
      </div>
      <article id="certificate" className="paper" aria-labelledby="cert-title">
        <p className="cert-eyebrow">
          frozen · issued {fmtDateTime(cert.issuedAt)} · PDP {frozen.version} · cert {cert.id}
          {cert.supersededBy ? <span className="state superseded">superseded</span> : null}
        </p>
        <h2 id="cert-title">
          Phase {cert.phase} exit · {gateLabel} · {DECISION_LABEL[frozen.decision]}
        </h2>
        <dl className="cert-facts">
          <dt>Product</dt>
          <dd>{frozen.productName}</dd>
          <dt>Approver</dt>
          <dd>
            {approverRoleName ?? <Missing />} (role) · recorded by {frozen.recordedBy.name}
          </dd>
          <dt>Review date</dt>
          <dd>
            {fmtDate(frozen.reviewDate)}
            {frozen.minutesUrl ? (
              <>
                {" · "}
                <a href={frozen.minutesUrl} target="_blank" rel="noopener">
                  minutes
                </a>
              </>
            ) : null}
          </dd>
          <dt>Conditions</dt>
          <dd>{frozen.conditions ?? "—"}</dd>
          <dt>Phase complete</dt>
          <dd>{frozen.percent === null ? "nothing owed" : `${frozen.percent}% · ${countsLine(frozen.counts)}`}</dd>
          <dt>Exit criteria</dt>
          <dd>
            {frozen.criteria.length ? (
              <ul className="cert-criteria">
                {frozen.criteria.map((c) => (
                  <li key={c.id}>
                    {c.area}: {c.text ?? <Missing />} ({stateText(c.evidenceState)})
                  </li>
                ))}
              </ul>
            ) : (
              "—"
            )}
          </dd>
          <dt>KPI feed values</dt>
          <dd>
            {fed.length ? (
              <ul className="cert-feeds">
                {fed.map((c) => (
                  <li key={c.id}>{feedReadingLine(c.feedReading!, c.target, c.measure ?? c.area)}</li>
                ))}
              </ul>
            ) : (
              "—"
            )}
          </dd>
        </dl>
        <table className="cert-rows">
          <thead>
            <tr>
              <th>Owed deliverable</th>
              <th>Phase</th>
              <th>Owner → Reviewer</th>
              <th>State at issue</th>
              <th>Pointer at issue</th>
            </tr>
          </thead>
          <tbody>
            {frozen.rows.map((r) => {
              const p = pointerWords(r.pointer, feedSourceLabels);
              return (
                <tr key={`${r.deliverableId}@${r.phase}`}>
                  <td>{r.name}</td>
                  <td className="cert-phase">{r.phase}</td>
                  <td>
                    <Roles ids={r.ownerRoleIds} names={roleNames} /> → <Roles ids={r.reviewerRoleIds} names={roleNames} />
                  </td>
                  <td>
                    <StateChip state={r.state} percent={r.pointer?.kind === "estimate" ? r.pointer.percent : undefined} />
                  </td>
                  <td className="cert-pointer">
                    {p.href ? (
                      <a href={p.href} target="_blank" rel="noopener">
                        {p.text}
                      </a>
                    ) : (
                      p.text
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="cert-foot">Pointers are recorded as they were; a link that later dies still shows what it pointed at and when.</p>
      </article>
    </div>
  );
}
